/**
 * «شهرم را از موقعیتم پیدا کن» — تنه‌ی مشترکِ دکمه‌ای که در فرمِ ساختِ نمایه
 * روی هر دو اپ هست.
 *
 * چرا این‌جا و نه دو بار در دو اپ: گرفتنِ خودِ مختصات پلتفرمی است
 * (`expo-location` آن‌طرف، `navigator.geolocation` این‌طرف) و به همین دلیل
 * در هوکِ `useMyLocation` هر اپ مانده. ولی آنچه **بعد** از مختصات می‌آید —
 * پیدا کردنِ نزدیک‌ترین شهر، و اینکه هر شکست کدام پیام را می‌خواهد — یک
 * منطق است و دو نسخه‌اش دیر یا زود از هم می‌افتند.
 *
 * ورودی عمداً یک تابعِ «مختصات را بده» است نه خودِ هوک: core نباید بداند
 * موقعیت از کجا می‌آید، و تست هم بتواند یک تابعِ ساده جایش بگذارد.
 */
import { geoRepo } from '../data/geoRepo.ts';
import { reportError } from './report.ts';
import type { City, Country } from '../domain/types.ts';

export interface Coords { lat: number; lng: number }

/**
 * نتیجه‌ی تلاش. سه شکستِ متفاوت عمداً از هم جدا شده‌اند، چون پیامشان یکی
 * نیست: «نه گفتی» راهنماییِ تنظیمات می‌خواهد، «نشد» فقط یک دوباره امتحان
 * کردن، و «شهری پیدا نشد» یعنی برو از فهرست انتخاب کن.
 */
export type FindCityResult =
  | { ok: true; city: City; country: Country }
  | { ok: false; reason: 'denied' | 'failed' | 'noCity' };

export async function findMyCity(
  getCoords: () => Promise<Coords | null>,
): Promise<FindCityResult> {
  let coords: Coords | null;
  try {
    coords = await getCoords();
  } catch (e) {
    reportError('findMyCity.coords', e);
    return { ok: false, reason: 'failed' };
  }
  // هوکِ هر دو اپ برای «نه گفت» و «نشد» یکسان `null` می‌دهد و تفاوتش را در
  // `state` نگه می‌دارد؛ صداکننده اگر آن را داشت، خودش `denied` را می‌گوید.
  if (!coords) return { ok: false, reason: 'denied' };

  try {
    const hit = await geoRepo.nearestCity(coords.lat, coords.lng);
    return hit ? { ok: true, ...hit } : { ok: false, reason: 'noCity' };
  } catch (e) {
    reportError('findMyCity.nearest', e);
    return { ok: false, reason: 'failed' };
  }
}
